<template>
  <v-card color="primary" class="rounded-sm">
    <v-card-text>
      <v-container>
        <v-form v-model="movimientoForm" ref="form">
          <v-row>
            <v-col cols="12">
              <BaseSelect
                class="ui-form-field"
                v-model="movimientoArma.fk_idcatTipoMovimiento"
                :items="tipoMovimiento"
                :rules="required"
              >
                <template v-slot:label>Tipo de movimiento <span class="red--text">*</span> </template>
              </BaseSelect>
              <BaseSelect
                class="ui-form-field"
                v-model="marca"
                @change="triggerFetchModeloArmas($event)"
                :items="marcaArma"
                :rules="required"
              >
                <template v-slot:label>Marca <span class="red--text">*</span> </template>
              </BaseSelect>
              <BaseSelect
                class="ui-form-field"
                v-model="movimientoArma.fk_idCatModelo"
                :items="catalogoModeloArma"
                :disabled="loadingModelos"
                :rules="required"
              >
                <template v-slot:label>Modelo <span class="red--text">*</span> </template>
              </BaseSelect>
              <v-menu
                v-model="menuFechaCompra"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <BaseInput
                    class="ui-form-field"
                    :value="fechaCompraFormatted"
                    append-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    hint="Formato de fecha: DD/MM/YYYY"
                    :rules="required"
                  >
                    <template v-slot:label>
                      {{ esSustraccion ? 'Fecha de movimiento' : 'Fecha de compra' }}
                      <span class="red--text">*</span>
                    </template>
                  </BaseInput>
                </template>
                <v-date-picker
                  v-model="fechaCompraString"
                  @input="menuFechaCompra = false"
                  locale="es"
                  no-title
                ></v-date-picker>
              </v-menu>
              <v-menu
                v-if="!esSustraccion"
                v-model="menuPuestaDisposicion"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <BaseInput
                    class="ui-form-field"
                    :value="puestaDisposicionFormatted"
                    append-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    hint="Formato de fecha: DD/MM/YYYY"
                    :rules="required"
                  >
                    <template v-slot:label>Fecha de puesta a disposición <span class="red--text">*</span> </template>
                  </BaseInput>
                </template>
                <v-date-picker
                  v-model="puestaDisposicionString"
                  @input="menuPuestaDisposicion = false"
                  locale="es"
                  no-title
                ></v-date-picker>
              </v-menu>
              <BaseInput
                class="ui-form-field"
                v-model.number="movimientoArma.cantidad"
                type="number"
                min="0"
                :rules="cantidadRules"
              >
                <template v-slot:label>Cantidad <span class="red--text">*</span> </template>
              </BaseInput>
              <v-textarea
                class="ui-form-field"
                label="Observaciones"
                v-model="movimientoArma.observaciones"
                no-resize
                rows="4"
                rounded
                filled
                dense
                color="secondary"
                background-color="transparent"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </v-card-text>
    <v-card-actions class="justify-center ui-module-actions">
      <TextButton
        class="ui-module-btn-solid"
        :disabled="loading || !movimientoForm"
        @click="triggerSaveMovimientoArma()"
        :loading="loading"
        >Registrar movimiento</TextButton
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapActions } from 'vuex'
import { API_DATE_FORMAT, VUETIFY_DATE_FORMAT } from '../../../../const'
import BaseSelect from '../../../base/BaseSelect.vue'
import BaseInput from '../../../base/BaseInput.vue'
import TextButton from '../../../base/TextButton.vue'

const normalize = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')

export default {
  components: {
    BaseSelect,
    BaseInput,
    TextButton,
  },
  data() {
    return {
      movimientoForm: false,
      loading: false,
      loadingModelos: false,
      marca: null,
      menuFechaCompra: false,
      menuPuestaDisposicion: false,
      // Nombre interno `fechaCompra` conservado; en sustracción representa la "Fecha de movimiento".
      movimientoArma: {
        fk_idCatModelo: null,
        fk_idcatTipoMovimiento: null,
        cantidad: 0,
        observaciones: '',
        fechaCompra: null,
        puestaDisposición: null,
      },
      cantidadRules: [(v) => v > 0 || 'La cantidad no puede ser cero.'],
      required: [(v) => !!v || 'Este campo es requerido.'],
    }
  },
  computed: {
    ...mapGetters('marcaArma', {
      marcaArma: 'getMarcaArma',
    }),
    ...mapGetters('modeloArma', {
      modeloArma: 'getModelosArmas',
      catalogoModeloArma: 'getCatalogoModeloArma',
    }),

    ...mapGetters('tipoMovimientoEquipamiento', {
      tipoMovimiento: 'getTipoMovimiento',
    }),
    tipoMovimientoSeleccionado() {
      return this.tipoMovimiento.find((tipo) => tipo.id === this.movimientoArma.fk_idcatTipoMovimiento) || null
    },
    esSustraccion() {
      if (!this.tipoMovimientoSeleccionado) return false
      return normalize(this.tipoMovimientoSeleccionado.title) === 'sustraccion'
    },
    fechaCompraFormatted() {
      return this.movimientoArma.fechaCompra || ''
    },
    fechaCompraString: {
      get() {
        return this.movimientoArma.fechaCompra
          ? moment(this.movimientoArma.fechaCompra, API_DATE_FORMAT).format(VUETIFY_DATE_FORMAT)
          : ''
      },
      set(value) {
        this.movimientoArma.fechaCompra = value ? moment(value, VUETIFY_DATE_FORMAT).format(API_DATE_FORMAT) : null
      },
    },
    puestaDisposicionFormatted() {
      return this.movimientoArma.puestaDisposición || ''
    },
    puestaDisposicionString: {
      get() {
        return this.movimientoArma.puestaDisposición
          ? moment(this.movimientoArma.puestaDisposición, API_DATE_FORMAT).format(VUETIFY_DATE_FORMAT)
          : ''
      },
      set(value) {
        this.movimientoArma.puestaDisposición = value
          ? moment(value, VUETIFY_DATE_FORMAT).format(API_DATE_FORMAT)
          : null
      },
    },
  },
  methods: {
    ...mapActions('modeloArma', ['fetchModeloArmas']),
    ...mapActions('movimientoArma', ['saveMovimientoArma', 'fetchMovimientoArma']),
    ...mapActions('arma', ['fetchArmas']),

    async triggerFetchModeloArmas(event) {
      this.loadingModelos = true
      await this.fetchModeloArmas(event)
      this.loadingModelos = false
    },
    async triggerSaveMovimientoArma() {
      this.loading = true
      if (this.esSustraccion) {
        this.movimientoArma.puestaDisposición = this.movimientoArma.fechaCompra
      }
      await this.saveMovimientoArma(this.movimientoArma)
      await this.fetchMovimientoArma()
      await this.fetchArmas()
    },
  },
}
</script>

<style scoped>
::v-deep .ui-form-field .v-input__slot {
  border-radius: 12px !important;
  border: 1px solid rgba(108, 92, 231, 0.25) !important;
  background: rgba(16, 16, 30, 0.35) !important;
}

::v-deep .ui-form-field .v-input__slot:hover {
  border-color: rgba(108, 92, 231, 0.45) !important;
}

::v-deep .ui-form-field .v-input__control {
  background: transparent !important;
  border-radius: 12px;
  border: none !important;
  box-shadow: none !important;
}

::v-deep .ui-form-field.v-input--is-focused .v-input__control {
  border: none !important;
  box-shadow: none !important;
}

::v-deep .ui-form-field input,
::v-deep .ui-form-field textarea,
::v-deep .ui-form-field .v-select__selection,
::v-deep .ui-form-field .mdi-menu-down {
  color: #e0e0e0 !important;
}

::v-deep .ui-form-field .v-label {
  color: rgba(224, 224, 224, 0.7) !important;
}

::v-deep .ui-form-field.v-input--is-focused .v-label {
  color: #a29bfe !important;
}

::v-deep .ui-module-actions .v-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}
</style>
